import { BaseRepositoryV2 } from "@/repositories/base-repository-v2";
import type { Person } from "@/repositories/people/types";
import type {
  Environment,
  Process,
  ProcessDetail,
  ProcessDetailResponse,
  ProcessRepositoryMethods,
  SubprocessTrustAutoinviteResponse,
} from "@/repositories/process/types";
import type {
  ProcessPublic,
  PublicControlledRegisterPayload,
  PublicControlledRegisterResponse,
} from "@/repositories/process/types-core";
import RepositoryFactory from "@/repositories/repository-factory";
import type { RealmSliceState } from "@/store/realm-slice";

export default class ProcessRepository
  extends BaseRepositoryV2
  implements ProcessRepositoryMethods
{
  // It's important to declare every method here and assign them as arrow functions
  // in the constructor to avoid errors in the client components that use them.
  path: string;

  addPersonToProcess;
  getAutoEvaluations;
  getCompletedProcesses;
  getExpiredProcesses;
  getOpenProcesses;
  getProcessDetail;
  getProcessEnvironment;
  getProcessPublic;
  postPublicControlledRegister;
  getProcesses;
  getSubprocessDetail;
  getSubprocessTrustAutoinviteStatus;

  constructor() {
    super();
    this.path = "/process";

    this.addPersonToProcess = async (person: Partial<Person>, processId: Process["id"]) => {
      /**
       * Makes a POST request to add a person to a specific process. This method is used when
       * clicking a mandatory process in the process list page.
       * @param {string} processId - The ID of the process to add the person to
       * @param {Person} person - The person object to be added to the process
       * @returns {Promise<boolean>} A promise that resolves with the response data
       * @throws {Error} If the request fails
       */
      const response = await this.post(`${this.path}/${processId}/people`, person, {}, true);
      return this.toAxiosResponse<boolean>(response);
    };

    /**
     * Makes a GET request to fetch auto evaluations the user can buy for the current logged user.
     * Uses the core API URL with /process-web path.
     * @returns {Promise<AutoEvaluationsResponseType[]>} The response containing an array of auto evaluations.
     * @throws {Error} If the request fails.
     */
    this.getAutoEvaluations = async () => {
      // Create a core repository instance for this specific call
      const coreRepo = RepositoryFactory.getCoreInstance();
      const response = await coreRepo.get("/process-web/process/me/process/public", {}, true);
      return response.json();
    };

    this.getCompletedProcesses = async () => {
      const response = await this.get(`${this.path}/v2/user/tests?status=COMPLETED`, {}, true);
      return this.toAxiosResponse<Process[]>(response);
    };

    this.getExpiredProcesses = async () => {
      const response = await this.get(`${this.path}/v2/user/tests?status=EXPIRED`, {}, true);
      return this.toAxiosResponse<Process[]>(response);
    };

    this.getOpenProcesses = async () => {
      const response = await this.get(`${this.path}/v2/user/tests?status=OPEN`, {}, true);
      return this.toAxiosResponse<Process[]>(response);
    };

    /**
     * Retrieves process test details.
     * @param processId - The ID of the process to retrieve details for.
     * @param realm - The provider realm for authentication.
     * @returns {Promise<ProcessDetailResponse>} A promise that resolves with the process test details.
     * @throws {Error} If the request fails.
     */
    this.getProcessDetail = async (processId: string, realm: RealmSliceState["realm"]) => {
      // TODO: use the Adonis endpoint once it has been migrated
      const coreRepo = RepositoryFactory.getCoreInstance();
      const response = await coreRepo.getWithRealm(
        `process-web/process/me/${processId}/tests`,
        realm || ""
      );
      return coreRepo.toAxiosResponse<ProcessDetailResponse>(response);
    };

    this.getProcessEnvironment = async (processUrl: string) => {
      const response = await this.get(`${this.path}/public/environment/${processUrl}`, {}, false);
      return this.toAxiosResponse<Environment>(response);
    };

    this.getProcessPublic = async (processId: string) => {
      const response = await this.get(
        `${this.path}/public/invitations/controlled/${processId}`,
        {},
        false
      );
      const axiosLike = await this.toAxiosResponse<{ data: ProcessPublic }>(response);
      return {
        ...axiosLike,
        data: axiosLike.data?.data as ProcessPublic,
      };
    };

    this.postPublicControlledRegister = async (
      processId: string,
      payload: PublicControlledRegisterPayload
    ) => {
      const response = await this.post<PublicControlledRegisterPayload>(
        `${this.path}/public/controlled/${processId}/people`,
        payload,
        {},
        false
      );
      const axiosLike = await this.toAxiosResponse<{ data: PublicControlledRegisterResponse }>(
        response
      );
      return {
        ...axiosLike,
        data: axiosLike.data?.data as PublicControlledRegisterResponse,
      };
    };

    this.getProcesses = async (name: string, status: "OPEN" | "COMPLETED" | "EXPIRED" = "OPEN") => {
      // Construir la URL base con el parámetro status
      let url = `${this.path}/v2/user/tests?status=${status}`;

      // Solo agregar el parámetro name si tiene un valor no vacío
      if (name && name.trim() !== "") {
        url += `&name=${encodeURIComponent(name)}`;
      }

      const response = await this.get(url, {}, true);
      return this.toAxiosResponse<Process[]>(response);
    };

    /**
     * Checks the trust autoinvite status for a subprocess.
     * @param subprocessId - The ID of the subprocess to check the status for
     * @param userId - The ID of the user to check the status for
     * @returns {Promise<void>} A promise that resolves when the request is successful
     * @throws {Error} If the request fails
     */
    this.getSubprocessTrustAutoinviteStatus = async (
      subprocessId: string,
      userId: string,
      realm: RealmSliceState["realm"]
    ) => {
      const coreRepo = RepositoryFactory.getCoreInstance();
      const response = await coreRepo.get(
        `/process-web/process/${subprocessId}/people/trust/${userId}`,
        { Realm: realm || "" },
        true
      );
      return coreRepo.toAxiosResponse<SubprocessTrustAutoinviteResponse>(response);
    };

    /**
     * Fetches subprocess test details from the API using the specified subprocess ID.
     * @param subprocessId - The ID of the subprocess to retrieve test details for
     * @returns {Promise<ProcessDetail>} A promise that resolves to the subprocess test details
     * @throws {Error} If the API request fails
     */
    this.getSubprocessDetail = async (subprocessId: string) => {
      const response = await this.get(`${this.path}/me/subprocess/${subprocessId}/tests`, {}, true);
      return this.toAxiosResponse<ProcessDetail>(response);
    };
  }
}
